import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Download, FileText, Calendar, Loader2, Lock, Check, Pencil, Mail, MessageCircle } from 'lucide-react';
import { toast } from '../../hooks/use-toast';
import EOSBPaymentSection from '../employee-form/EOSBPaymentSection';
import EmailDocDialog from './EmailDocDialog';
import { openWhatsApp, getWhatsAppNumber } from '../../utils/whatsapp';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// EOSB Preview Component
export const EOSBPreviewDialog = ({ open, onClose, employee, onEmployeeUpdate }) => {
  const [calculationDate, setCalculationDate] = useState(new Date().toISOString().split('T')[0]);
  const [eosbData, setEosbData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [finalizing, setFinalizing] = useState(false);
  const [isDateFinalized, setIsDateFinalized] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [emailOpen, setEmailOpen] = useState(false);
  const [settlementType, setSettlementType] = useState('standard');
  const [savingScenario, setSavingScenario] = useState(false);

  useEffect(() => {
    if (open && employee) {
      setSettlementType(employee.eosb_settlement_type || 'standard');
      // Check if employee has a finalized EOSB date
      if (employee.eosb_finalized_date) {
        setCalculationDate(employee.eosb_finalized_date);
        setIsDateFinalized(true);
        setIsEditing(false);
      } else if (employee.last_working_date) {
        // Default to last_working_date if set
        setCalculationDate(employee.last_working_date.split('T')[0]);
        setIsDateFinalized(false);
      } else {
        setCalculationDate(new Date().toISOString().split('T')[0]);
        setIsDateFinalized(false);
      }
    }
  }, [open, employee]);

  useEffect(() => {
    if (open && employee && calculationDate) {
      fetchEOSB();
    }
  }, [open, employee, calculationDate, settlementType]);

  const fetchEOSB = async () => {
    if (!employee) return;
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/dashboard/eosb/${employee.id}?calculation_date=${calculationDate}&settlement_type=${settlementType}`);
      if (!response.ok) throw new Error('Failed to fetch EOSB');
      const data = await response.json();
      setEosbData(data);
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to calculate EOSB', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const changeScenario = async (val) => {
    setSettlementType(val); // triggers refetch via effect
    setSavingScenario(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/employees/${employee.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ eosb_settlement_type: val }),
      });
      if (!response.ok) throw new Error('Failed to save scenario');
      if (onEmployeeUpdate) onEmployeeUpdate();
    } catch (e) {
      toast({ title: 'Error', description: 'Could not save settlement scenario', variant: 'destructive' });
    } finally {
      setSavingScenario(false);
    }
  };

  const handleFinalizeDate = async () => {
    setFinalizing(true);
    try {
      const response = await fetch(`${API_URL}/api/employees/${employee.id}/finalize-eosb-date`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ eosb_finalized_date: calculationDate })
      });
      
      if (!response.ok) throw new Error('Failed to finalize date');
      
      setIsDateFinalized(true);
      setIsEditing(false);
      toast({ 
        title: 'Date Finalized', 
        description: `EOSB calculation date locked to ${new Date(calculationDate).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' })}` 
      });
      
      // Notify parent to refresh employee data
      if (onEmployeeUpdate) onEmployeeUpdate();
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to finalize date', variant: 'destructive' });
    } finally {
      setFinalizing(false);
    }
  };

  const handleEditDate = () => {
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    // Restore the finalized date
    if (employee.eosb_finalized_date) {
      setCalculationDate(employee.eosb_finalized_date);
    }
    setIsEditing(false);
  };

  const handleDownload = async (format) => {
    setDownloading(true);
    try {
      const response = await fetch(
        `${API_URL}/api/downloads/eosb/${employee.id}?format=${format}&calculation_date=${calculationDate}&settlement_type=${settlementType}`
      );
      if (!response.ok) throw new Error('Download failed');
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `EOSB_${employee.employee_code}_${calculationDate}.${format === 'excel' ? 'xlsx' : 'pdf'}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast({ title: 'Download Complete', description: `EOSB ${format.toUpperCase()} downloaded` });
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to download EOSB', variant: 'destructive' });
    } finally {
      setDownloading(false);
    }
  };

  if (!employee) return null;

  const canEditDate = !isDateFinalized || isEditing;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5 text-blue-600" />
            EOSB Calculation - {employee.name}
          </DialogTitle>
        </DialogHeader>
        
        {/* Date Input Section */}
        <div className={`p-4 rounded-lg border ${isDateFinalized && !isEditing ? 'bg-green-50 border-green-200' : 'bg-blue-50 border-blue-200'}`}>
          <div className="flex items-center justify-between mb-2">
            <Label htmlFor="calcDate" className={`text-sm font-medium ${isDateFinalized && !isEditing ? 'text-green-800' : 'text-blue-800'}`}>
              <Calendar className="h-4 w-4 inline mr-2" />
              Last Working Date (for calculation)
            </Label>
            
            {/* Status Badge */}
            {isDateFinalized && !isEditing && (
              <span className="inline-flex items-center gap-1 text-xs font-medium text-green-700 bg-green-100 px-2 py-1 rounded-full">
                <Lock className="h-3 w-3" />
                Finalized
              </span>
            )}
          </div>
          
          <div className="flex items-center gap-3">
            <Input
              id="calcDate"
              type="date"
              value={calculationDate}
              onChange={(e) => setCalculationDate(e.target.value)}
              className="max-w-xs"
              disabled={!canEditDate}
              data-testid="eosb-date-input"
            />
            
            {/* Action Buttons */}
            {!isDateFinalized ? (
              <Button 
                onClick={handleFinalizeDate}
                disabled={finalizing || loading}
                size="sm"
                className="bg-green-600 hover:bg-green-700"
                data-testid="finalize-eosb-date-btn"
              >
                {finalizing ? (
                  <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                ) : (
                  <Check className="h-4 w-4 mr-1" />
                )}
                Finalize Date
              </Button>
            ) : isEditing ? (
              <div className="flex gap-2">
                <Button 
                  onClick={handleFinalizeDate}
                  disabled={finalizing || loading}
                  size="sm"
                  className="bg-green-600 hover:bg-green-700"
                  data-testid="save-eosb-date-btn"
                >
                  {finalizing ? (
                    <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                  ) : (
                    <Check className="h-4 w-4 mr-1" />
                  )}
                  Save
                </Button>
                <Button 
                  onClick={handleCancelEdit}
                  variant="outline"
                  size="sm"
                  data-testid="cancel-edit-eosb-date-btn"
                >
                  Cancel
                </Button>
              </div>
            ) : (
              <Button 
                onClick={handleEditDate}
                variant="outline"
                size="sm"
                data-testid="edit-eosb-date-btn"
              >
                <Pencil className="h-4 w-4 mr-1" />
                Edit Date
              </Button>
            )}
          </div>
          
          <p className={`text-xs mt-2 ${isDateFinalized && !isEditing ? 'text-green-600' : 'text-blue-600'}`}>
            {isDateFinalized && !isEditing 
              ? 'This date is finalized and will be used for all EOSB reports.'
              : 'Select the last working date to calculate EOSB. Click "Finalize Date" to lock this date for all reports.'}
          </p>
        </div>

        {/* Preview Content */}
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
            <span className="ml-2">Calculating...</span>
          </div>
        ) : eosbData ? (
          <div className="space-y-4">
            {/* Employee Info */}
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-gray-50 p-3 rounded-lg">
                <p className="text-xs text-gray-500">Employee Code</p>
                <p className="font-semibold">{employee.employee_code}</p>
              </div>
              <div className="bg-gray-50 p-3 rounded-lg">
                <p className="text-xs text-gray-500">Join Date</p>
                <p className="font-semibold">{new Date(employee.join_date).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' })}</p>
              </div>
            </div>

            {/* Service & Salary Info */}
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-gray-50 p-3 rounded-lg">
                <p className="text-xs text-gray-500">Years of Service</p>
                <p className="text-lg font-semibold">{eosbData.years_of_service?.toFixed(2)} years</p>
              </div>
              <div className="bg-gray-50 p-3 rounded-lg">
                <p className="text-xs text-gray-500">Basic Salary</p>
                <p className="text-lg font-semibold">AED {eosbData.basic_salary?.toLocaleString()}</p>
              </div>
            </div>

            {/* Settlement scenario */}
            <div className="bg-slate-50 p-3 rounded-lg border border-slate-200">
              <Label className="text-xs text-gray-500">Settlement Scenario</Label>
              <Select value={settlementType} onValueChange={changeScenario} disabled={savingScenario}>
                <SelectTrigger className="mt-1" data-testid="eosb-scenario-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="standard">Standard — gratuity payable</SelectItem>
                  <SelectItem value="absconding">Absconding — reserve, do not pay</SelectItem>
                  <SelectItem value="no_notice">Resigned without notice — deduct 1 month salary</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* EOSB Amount — display adapts to the settlement scenario */}
            {settlementType === 'absconding' ? (
              <div className="bg-red-50 p-4 rounded-lg border-2 border-red-300" data-testid="eosb-amount-absconding">
                <p className="text-sm font-semibold text-red-700">⚠ ABSCONDING — RESERVED (NOT DISBURSED)</p>
                <p className="text-3xl font-bold text-red-600 mt-1">
                  AED {(eosbData.reserved_amount ?? eosbData.eosb_amount)?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </p>
                <p className="text-xs text-red-700/80 mt-1">Held in the company account. Net paid to employee: AED 0.00</p>
              </div>
            ) : settlementType === 'no_notice' ? (
              <div className="bg-amber-50 p-4 rounded-lg border border-amber-300 space-y-1" data-testid="eosb-amount-no-notice">
                <div className="flex justify-between text-sm"><span className="text-gray-600">Gratuity</span><span className="font-mono">AED {eosbData.eosb_amount?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span></div>
                <div className="flex justify-between text-sm"><span className="text-gray-600">Less: 1 month salary (no notice)</span><span className="font-mono text-rose-700">− AED {eosbData.notice_deduction?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span></div>
                {eosbData.staff_owes > 0 ? (
                  <div className="flex justify-between border-t border-amber-300 pt-1 font-bold text-rose-700">
                    <span>Employee owes company</span>
                    <span className="font-mono text-2xl">AED {eosbData.staff_owes?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                  </div>
                ) : (
                  <div className="flex justify-between border-t border-amber-300 pt-1 font-bold text-green-700">
                    <span>Net payable</span>
                    <span className="font-mono text-2xl">AED {eosbData.net_payable?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                  </div>
                )}
              </div>
            ) : (
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <p className="text-sm text-gray-600">EOSB Amount</p>
                <p className="text-3xl font-bold text-green-600">
                  AED {eosbData.eosb_amount?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </p>
              </div>
            )}

            {/* Calculation Details */}
            <div className="bg-gray-50 p-3 rounded-lg">
              <p className="text-xs text-gray-500 mb-2">Calculation Breakdown</p>
              <pre className="text-xs whitespace-pre-wrap font-mono bg-white p-2 rounded border max-h-40 overflow-y-auto">
                {eosbData.calculation_details}
              </pre>
            </div>

            {/* EOSB Payment Section - Only for Resigned/Terminated employees */}
            {['Resigned', 'Terminated'].includes(employee.employment_status) && (
              <EOSBPaymentSection
                employee={employee}
                eosbAmount={eosbData.eosb_amount}
                onUpdate={() => fetchEOSB()}
              />
            )}
          </div>
        ) : null}

        <DialogFooter className="flex gap-2 mt-4">
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
          <Button
            variant="outline"
            onClick={() => setEmailOpen(true)}
            disabled={loading || !eosbData}
            className="text-indigo-600 border-indigo-200 hover:bg-indigo-50"
            data-testid="eosb-email-staff-btn"
          >
            <Mail className="h-4 w-4 mr-2" /> Email to Staff
          </Button>
          <Button
            variant="outline"
            onClick={() => openWhatsApp(
              getWhatsAppNumber(employee),
              `Hi ${employee.name || ''}, sharing your End of Service Benefits (EOSB) settlement statement. Please find it attached.`
            )}
            className="text-green-700 border-green-200 hover:bg-green-50"
            data-testid="eosb-whatsapp-btn"
          >
            <MessageCircle className="h-4 w-4 mr-2" /> WhatsApp
          </Button>
          <Button
            variant="outline"
            onClick={() => handleDownload('excel')}
            disabled={downloading || loading}
          >
            {downloading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />}
            Excel
          </Button>
          <Button
            onClick={() => handleDownload('pdf')}
            disabled={downloading || loading}
          >
            {downloading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />}
            Download PDF
          </Button>
        </DialogFooter>
      </DialogContent>
      <EmailDocDialog
        open={emailOpen}
        onOpenChange={setEmailOpen}
        title="Email Final Settlement (EOSB)"
        recipientName={employee?.name}
        attachmentLabel="EOSB statement"
        payload={{ doc_type: 'eosb', employee_id: employee?.id, calculation_date: calculationDate }}
      />
    </Dialog>
  );
};

export default EOSBPreviewDialog;
